import React, { useState, useMemo, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import DataRetentionTable from './components/DataRetentionTable';
import { DataRetentionDialog } from './components/DataRetentionDialog';
import SearchBar from './components/SearchBar';
import { DataRetentionCreateDialog } from './components/DataRetentionCreateDialog';
import { toUI, blankDataRetentionRecordUI, fromUI } from './dataTypeMapping';
import { findLinkedAssetsAndSuppliers, getAssetIdToNameMap } from './assetLinkingUtil';
import { DataRetentionRecord } from './mockDataRetentionRecords';
import dataRetentionService, { DataPool, getDataPools, saveDataPool, deleteDataPool } from '@/services/dataRetentionService';
import { DataRetentionSchedule as DataRetentionScheduleNamespace } from '@/js/external-js/variableModules/namespaces/DataRetentionSchedule.namespace';
import { useTenancy } from '@/contexts/TenancyContext';
import './dataRetentionSchedule.css';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Trash2, Edit2, Plus, Database, Layers } from 'lucide-react';
import { toast } from 'sonner';
import { DataTypeCombobox, DataTypesProvider } from '@/components/ui/DataTypeCombobox';
import { AssetsCombobox, AssetsProvider } from '@/components/ui/AssetsCombobox';
import { isFeatureEnabledForPlan } from '@/utils/featuresUtil';

interface AssetDataTypeSource {
  assetID: string | number;
  dataTypesShared?: Array<string | number> | null;
  dataTypesSharedString?: string | null;
}

const normalizeComparisonValue = (value: string) => value.trim().toLowerCase().replace(/\s+/g, ' ');

const deriveNameFromReference = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed) return '';
  const withoutSpecificIds = trimmed
    .replace(/\((?:id|#)?\s*\d+\)/gi, ' ')
    .replace(/\b(?:id|ref|reference|#)\s*\d+\b/gi, ' ');
  const withoutGenericParens = withoutSpecificIds.replace(/\([^)]*\)/g, ' ');
  const cleaned = withoutGenericParens.replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
  return cleaned || trimmed;
};

const gatherDataTypeReferences = (assets: AssetDataTypeSource[]) => {
  const refs = new Set<string>();
  assets.forEach(asset => {
    if (Array.isArray(asset.dataTypesShared)) {
      asset.dataTypesShared.forEach(entry => {
        if (entry === undefined || entry === null) return;
        const value = String(entry).trim();
        if (value) refs.add(value);
      });
    }
    if (typeof asset.dataTypesSharedString === 'string') {
      asset.dataTypesSharedString
        .split(/[;,\n]/)
        .map(chunk => chunk.trim())
        .filter(Boolean)
        .forEach(value => refs.add(value));
    }
  });
  return refs;
};

const extractMatchingExistingId = (ref: string, existingIds: Set<string>) => {
  const matches = ref.match(/\d+/g);
  if (!matches) return null;
  for (const raw of matches) {
    const parsed = Number(raw);
    if (!Number.isNaN(parsed) && existingIds.has(String(parsed))) {
      return String(parsed);
    }
  }
  return null;
};

const pickCandidateId = (ref: string, existingIds: Set<string>, reservedIds: Set<string>) => {
  const matches = ref.match(/\d+/g);
  if (!matches) return null;
  for (const raw of matches) {
    const parsed = Number(raw);
    if (Number.isNaN(parsed)) continue;
    const key = String(parsed);
    if (!existingIds.has(key) && !reservedIds.has(key)) {
      return parsed;
    }
  }
  return null;
};

const DataRetentionSchedule = () => {
  const { activeTenancy } = useTenancy();
  const [activeTab, setActiveTab] = useState<'types' | 'pools'>('types');
  const [search, setSearch] = useState('');
  const [records, setRecords] = useState<DataRetentionScheduleNamespace.DataTypeEntity[]>([]);
  const [recordsLoading, setRecordsLoading] = useState(true); // Loading state for data types
  const [showAdd, setShowAdd] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogRecord, setDialogRecord] = useState<DataRetentionScheduleNamespace.DataTypeEntity | null>(null);
  const [dialogLinkedAssets, setDialogLinkedAssets] = useState<string[]>([]);
  const { id: routeDataId } = useParams<{ id?: string }>();

  // Filter states
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [personalInfoFilter, setPersonalInfoFilter] = useState<'all' | 'yes' | 'no'>('all');

  // Data Pools state
  const [dataPools, setDataPools] = useState<DataPool[]>([]);
  const [poolSearch, setPoolSearch] = useState('');
  const [showAddPool, setShowAddPool] = useState(false);
  const [editingPool, setEditingPool] = useState<DataPool | null>(null);
  const [poolForm, setPoolForm] = useState<Partial<DataPool>>({ name: '', reason: '', status: 'active', dataTypes: [], systems: [] });
  const [assetIdToName, setAssetIdToName] = useState<Map<string, string>>(new Map());
  const [assetNameToId, setAssetNameToId] = useState<Map<string, string>>(new Map());
  const [poolsLoading, setPoolsLoading] = useState(false);

  const filteredRecords = useMemo(() => {
    const lower = search.toLowerCase();

    return records.filter(r => {
      // Hide soft-deleted records (deleted=true means permanently hidden)
      if (r.deleted === true) return false;

      const matchesText =
        (typeof r.dataTypeName === 'string' && r.dataTypeName.toLowerCase().includes(lower)) ||
        (typeof r.dataTypeJustification === 'string' && r.dataTypeJustification.toLowerCase().includes(lower));

      // Status filter (active/inactive based on status field)
      const matchesStatus = statusFilter === 'all' ||
        (statusFilter === 'active' && r.status !== 'inactive') ||
        (statusFilter === 'inactive' && r.status === 'inactive');

      // Personal information filter
      const matchesPersonalInfo = personalInfoFilter === 'all' ||
        (personalInfoFilter === 'yes' && r.isPersonalInformation === true) ||
        (personalInfoFilter === 'no' && r.isPersonalInformation !== true);

      return matchesText && matchesStatus && matchesPersonalInfo;
    });
  }, [search, records, statusFilter, personalInfoFilter]);

  // Button style for active filter
  const getFilterButtonClass = (active: boolean) =>
    active
      ? 'filter-btn-active border font-semibold bg-primary-fade text-[color:var(--primary)] border-primary-fade-border'
      : 'border border-border bg-transparent text-foreground';

  const handleAdd = async (record: DataRetentionScheduleNamespace.DataTypeEntity) => {
    // Optimistic update: immediately add to local state
    setRecords(prev => [...prev, record]);
    
    // Sync with server in background
    try {
      await dataRetentionService.createDataType(record.dataTypeID, record);
    } catch (error) {
      console.error('Failed to create data type:', error);
      toast.error('Failed to save - please try again');
      // Revert optimistic update on error
      setRecords(prev => prev.filter(r => r.dataTypeID !== record.dataTypeID));
    }
  };

  const ensureAssetReferencedDataTypes = React.useCallback(async (existing: DataRetentionScheduleNamespace.DataTypeEntity[]) => {
    console.log('[DataRetentionSchedule] ensureAssetReferencedDataTypes:start', { existingCount: existing.length, tenancyId: activeTenancy?.id });
    if (!activeTenancy || !activeTenancy.id || ['', 'Loading...'].includes(activeTenancy.id)) {
      console.log('[DataRetentionSchedule] ensureAssetReferencedDataTypes:skip-no-tenancy');
      return false;
    }
    const { getAssets } = await import('@/services/assetsService');
    const assets = await getAssets();
    console.log('[DataRetentionSchedule] ensureAssetReferencedDataTypes:assets-fetched', { count: assets?.length ?? 0 });
    if (!assets || assets.length === 0) {
      console.log('[DataRetentionSchedule] ensureAssetReferencedDataTypes:skip-no-assets');
      return false;
    }

    const references = gatherDataTypeReferences(assets as AssetDataTypeSource[]);
    console.log('[DataRetentionSchedule] ensureAssetReferencedDataTypes:references-extracted', { count: references.size });
    if (!references.size) {
      console.log('[DataRetentionSchedule] ensureAssetReferencedDataTypes:skip-no-references');
      return false;
    }

    const existingIds = new Set(existing.map(rec => String(rec.dataTypeID)));
    const comparisonNames = new Set<string>();
    existing.forEach(rec => {
      if (!rec?.dataTypeName) return;
      const base = normalizeComparisonValue(rec.dataTypeName);
      if (base) comparisonNames.add(base);
    });

    const alreadyAligned = new Set<string>();
    references.forEach(ref => {
      const normalized = normalizeComparisonValue(deriveNameFromReference(ref));
      if (normalized && comparisonNames.has(normalized)) {
        alreadyAligned.add(ref);
      }
    });

    const missing = Array.from(references).filter(ref => !alreadyAligned.has(ref));
    console.log('[DataRetentionSchedule] ensureAssetReferencedDataTypes:missing-derived', { missingCount: missing.length, missingSamples: missing.slice(0, 10) });
    if (!missing.length) {
      console.log('[DataRetentionSchedule] ensureAssetReferencedDataTypes:skip-none-missing');
      return false;
    }

    let changed = false;
    const reservedIds = new Set<string>();

    for (const ref of missing) {
      const normalizedName = deriveNameFromReference(ref);
      if (!normalizedName) continue;

      const matchedExistingId = extractMatchingExistingId(ref, existingIds);
      if (matchedExistingId) {
        console.log('[DataRetentionSchedule] ensureAssetReferencedDataTypes:existing-id-detected', { ref, matchedExistingId });
        continue;
      }

      const candidateId = pickCandidateId(ref, existingIds, reservedIds);
      let newId = candidateId ?? Math.floor(Math.random() * 1000000);
      while (existingIds.has(String(newId)) || reservedIds.has(String(newId))) {
        newId = Math.floor(Math.random() * 1000000);
      }
      reservedIds.add(String(newId));

      const newUiRecord = {
        ...blankDataRetentionRecordUI(),
        dataTypeID: newId,
        dataTypeName: normalizedName,
        dataTypeJustification: normalizedName,
        dataTypeModeOfCollection: 'Not specified',
        isPersonalInformation: false,
        status: 'Active' as const,
      };

      const entity = fromUI(newUiRecord);

      await dataRetentionService.createDataType(entity.dataTypeID, entity);
      console.log('[DataRetentionSchedule] ensureAssetReferencedDataTypes:created-record', { ref, dataTypeID: entity.dataTypeID, dataTypeName: entity.dataTypeName });
      existingIds.add(String(entity.dataTypeID));
      changed = true;
    }

    console.log('[DataRetentionSchedule] ensureAssetReferencedDataTypes:completed', { changed });
    return changed;
  }, [activeTenancy?.id]);

  // Load records on mount and deduplicate asset/supplier links for all records
  // Track if initial load has completed (separate from loading state)
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  
  // Expose loadRecords for use outside effect
  const loadRecords = React.useCallback(async (showLoading = true) => {
    console.log('[DataRetentionSchedule] loadRecords:start', { tenancyId: activeTenancy?.id });
    if (!activeTenancy || !activeTenancy.id || ["", "Loading..."].includes(activeTenancy.id)) return;
    
    // Only show loading on initial load, not background refreshes
    if (showLoading) setRecordsLoading(true);
    
    try {
      const records = await dataRetentionService.getDataTypes();
      console.log('[DataRetentionSchedule] loadRecords:initial-fetch', { count: records.length });
      const created = await ensureAssetReferencedDataTypes(records);
      console.log('[DataRetentionSchedule] loadRecords:ensure-result', { created });
      const baseRecords = created ? await dataRetentionService.getDataTypes() : records;
      if (created) {
        console.log('[DataRetentionSchedule] loadRecords:post-create-fetch', { count: baseRecords.length });
      }
      const { findLinkedAssetsAndSuppliers } = await import('./assetLinkingUtil');
      const updated = await Promise.all(baseRecords.map(async rec => {
        const ui = toUI(rec);
        const deduped = await findLinkedAssetsAndSuppliers(ui);
        return { ...rec, assets: deduped, suppliers: deduped };
      }));
      console.log('[DataRetentionSchedule] loadRecords:dedupe-complete', { count: updated.length });
      setRecords(updated);
      setInitialLoadComplete(true);
      console.log('[DataRetentionSchedule] loadRecords:end');
    } finally {
      setRecordsLoading(false);
    }
  }, [activeTenancy?.id, ensureAssetReferencedDataTypes]);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  // Load data pools
  const loadDataPools = React.useCallback(async () => {
    if (!activeTenancy?.id || ['', 'Loading...'].includes(activeTenancy.id)) return;
    setPoolsLoading(true);
    try {
      const [pools, idToNameMap] = await Promise.all([
        getDataPools(activeTenancy.id),
        getAssetIdToNameMap()
      ]);
      setDataPools(pools);
      setAssetIdToName(idToNameMap);
      // Build reverse map (name -> id) for fast lookups
      const nameToIdMap = new Map<string, string>();
      idToNameMap.forEach((name, id) => {
        nameToIdMap.set(name.toLowerCase(), id);
      });
      setAssetNameToId(nameToIdMap);
    } catch (error) {
      console.error('Failed to load data pools:', error);
    } finally {
      setPoolsLoading(false);
    }
  }, [activeTenancy?.id]);

  useEffect(() => {
    loadDataPools();
  }, [loadDataPools]);

  const filteredPools = useMemo(() => {
    if (!poolSearch.trim()) return dataPools;
    const lower = poolSearch.toLowerCase();
    return dataPools.filter(p =>
      p.name.toLowerCase().includes(lower) ||
      (p.reason && p.reason.toLowerCase().includes(lower))
    );
  }, [poolSearch, dataPools]);

  // Helper to resolve system IDs to display names
  const getSystemDisplayNames = (systemIds: string[] | undefined): string[] => {
    if (!systemIds || systemIds.length === 0) return [];
    return systemIds.map(id => assetIdToName.get(id) || id).filter(Boolean);
  };

  // Helper to resolve data type IDs to display names
  const getDataTypeDisplayNames = (dataTypeIds: string[] | undefined): string[] => {
    if (!dataTypeIds || dataTypeIds.length === 0) return [];
    return dataTypeIds.map(id => {
      const found = records.find(r => String(r.dataTypeID) === id);
      return found?.dataTypeName || id;
    }).filter(Boolean);
  };

  // Build maps for data type lookups
  const dataTypeIdToName = useMemo(() => {
    const map = new Map<string, string>();
    records.forEach(r => map.set(String(r.dataTypeID), r.dataTypeName));
    return map;
  }, [records]);

  const dataTypeNameToId = useMemo(() => {
    const map = new Map<string, string>();
    records.forEach(r => map.set(r.dataTypeName.toLowerCase(), String(r.dataTypeID)));
    return map;
  }, [records]);

  // Synchronous helper to convert data type names/IDs to IDs
  const resolveDataTypeIdsSync = (dataTypes: string[]): string[] => {
    if (!dataTypes || dataTypes.length === 0) return [];
    const idSet = new Set(dataTypeIdToName.keys());
    return dataTypes.map(val => {
      // Already an ID
      if (idSet.has(val)) return val;
      // Try as a name (case-insensitive)
      const id = dataTypeNameToId.get(val.toLowerCase());
      return id || val;
    }).filter(Boolean);
  };

  const handleSavePool = async () => {
    if (!activeTenancy?.id) return;
    if (!poolForm.name?.trim()) {
      toast.error('Pool name is required');
      return;
    }
    try {
      const pool: DataPool = {
        id: editingPool?.id || poolForm.name.toLowerCase().replace(/\s+/g, '-') + '-' + Date.now(),
        name: poolForm.name,
        reason: poolForm.reason,
        dataTypes: poolForm.dataTypes || [],
        systems: poolForm.systems || [],
        suppliers: poolForm.suppliers || [],
        labels: poolForm.labels || [],
        status: poolForm.status || 'active'
      };
      await saveDataPool(activeTenancy.id, pool);
      toast.success(editingPool ? 'Data pool updated' : 'Data pool created');
      setShowAddPool(false);
      setEditingPool(null);
      setPoolForm({ name: '', reason: '', status: 'active', dataTypes: [], systems: [] });
      loadDataPools();
    } catch (error) {
      console.error('Failed to save data pool:', error);
      toast.error('Failed to save data pool');
    }
  };

  const handleDeletePool = async (poolId: string) => {
    if (!activeTenancy?.id) return;
    try {
      await deleteDataPool(activeTenancy.id, poolId);
      toast.success('Data pool deleted');
      loadDataPools();
    } catch (error) {
      console.error('Failed to delete data pool:', error);
      toast.error('Failed to delete data pool');
    }
  };

  // Synchronous helper to convert system names/IDs to IDs using pre-loaded maps
  const resolveSystemIdsSync = (systems: string[]): string[] => {
    if (!systems || systems.length === 0) return [];
    const idSet = new Set(assetIdToName.keys());
    return systems.map(val => {
      // Already an ID
      if (idSet.has(val)) return val;
      // Try as a name (case-insensitive)
      const id = assetNameToId.get(val.toLowerCase());
      return id || val;
    }).filter(Boolean);
  };

  const openEditPool = (pool: DataPool) => {
    setEditingPool(pool);
    // Convert any names to IDs for the comboboxes (synchronous using pre-loaded maps)
    const systemIds = resolveSystemIdsSync(pool.systems || []);
    const dataTypeIds = resolveDataTypeIdsSync(pool.dataTypes || []);
    setPoolForm({
      name: pool.name,
      reason: pool.reason,
      dataTypes: dataTypeIds,
      systems: systemIds,
      suppliers: pool.suppliers,
      labels: pool.labels,
      status: pool.status
    });
    setShowAddPool(true);
  };

  // Deep-link: open record dialog when /dataRegister/:id is visited
  useEffect(() => {
    const rid = String(routeDataId || '').trim();
    if (!rid || !Array.isArray(records) || records.length === 0) return;
    const match = records.find(r => String(r.dataTypeID) === rid);
    if (match) {
      setDialogRecord(match);
      setDialogLinkedAssets((match as any).assets ?? []);
      setDialogOpen(true);
    }
  }, [routeDataId, records]);

  const [showDeleteDataItemDialog, setShowDeleteDataItemDialog] = useState<{show: boolean, itemID: number | null}>({show: false, itemID: null});
  const deleteDataItem = async (confirmed: boolean): Promise<void> => {
    if (!confirmed) {
      setShowDeleteDataItemDialog({show: false, itemID: null});
      return;
    }

    const itemID = showDeleteDataItemDialog.itemID;
    if (itemID === null) {
      setShowDeleteDataItemDialog({show: false, itemID: null});
      return;
    }

    // Optimistic update: immediately remove from local state
    setRecords(prev => prev.map(r => 
      r.dataTypeID === itemID ? { ...r, deleted: true } : r
    ));
    setShowDeleteDataItemDialog({show: false, itemID: null});
    toast.success('Data type deleted');

    // Sync with server in background
    try {
      await dataRetentionService.deleteDataType(itemID);
      // Background refresh to ensure consistency (non-blocking, no loading indicator)
      loadRecords(false);
    } catch (error) {
      console.error('Failed to delete data type:', error);
      toast.error('Failed to sync deletion - please refresh');
      // Revert optimistic update on error
      setRecords(prev => prev.map(r => 
        r.dataTypeID === itemID ? { ...r, deleted: false } : r
      ));
    }
  };

  const onDeleteClick = (item: any) => {
    setShowDeleteDataItemDialog({show: true, itemID: item.dataTypeID});
  };


  return (
    <DataTypesProvider>
      <AssetsProvider>
        <div className="wrapper d-flex flex-column min-vh-100">
          <section className="main-container">
            <div className="glass-card">
              <h2 style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: 18 }}>
                Data Register
              </h2>
              
              <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'types' | 'pools')} className="mt-4">
            <TabsList className={`grid w-full max-w-md ${isFeatureEnabledForPlan('Data Pools', activeTenancy?.plan) ? 'grid-cols-2' : 'grid-cols-1'}`}>
              <TabsTrigger value="types" className="flex items-center gap-2">
                <Database className="h-4 w-4" />
                Data Types
              </TabsTrigger>
              {isFeatureEnabledForPlan('Data Pools', activeTenancy?.plan) && (
                <TabsTrigger value="pools" className="flex items-center gap-2">
                  <Layers className="h-4 w-4" />
                  Data Pools
                </TabsTrigger>
              )}
            </TabsList>

            <TabsContent value="types" className="mt-4">
              {!initialLoadComplete ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <Database className="h-12 w-12 mx-auto mb-4 opacity-50 animate-pulse" />
                    <p className="text-muted-foreground">Loading data types...</p>
                  </div>
                </div>
              ) : (
                <>
                  {/* Search, Filters, and Add Button - All on one line */}
                  <div className="flex flex-wrap items-center gap-3 mb-6">
                    {/* Search - takes up available space */}
                    <div className="flex-1 min-w-[200px]">
                      <SearchBar value={search} onChange={setSearch} />
                    </div>

                    {/* Filters and Add Button grouped on the right */}
                    <div className="flex flex-wrap items-center gap-2">
                      {/* All Data Types Button */}
                      <Button
                        variant="outline"
                        size="sm"
                        className={getFilterButtonClass(statusFilter === 'all' && personalInfoFilter === 'all') + ' h-9 px-4'}
                        onClick={() => {
                          setStatusFilter('all');
                          setPersonalInfoFilter('all');
                        }}
                      >
                        All Data Types
                      </Button>

                      {/* Status Dropdown */}
                      <Select value={statusFilter} onValueChange={v => setStatusFilter(v as typeof statusFilter)}>
                        <SelectTrigger className={getFilterButtonClass(statusFilter !== 'all') + ' min-w-[120px] h-9'}>
                          <span>
                            {statusFilter !== 'all' ? `Status: ${statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)}` : 'Status'}
                          </span>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All</SelectItem>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="inactive">Inactive</SelectItem>
                        </SelectContent>
                      </Select>

                      {/* Personal Information Dropdown */}
                      <Select value={personalInfoFilter} onValueChange={v => setPersonalInfoFilter(v as typeof personalInfoFilter)}>
                        <SelectTrigger className={getFilterButtonClass(personalInfoFilter !== 'all') + ' min-w-[160px] h-9'}>
                          <span>
                            {personalInfoFilter !== 'all' ? `Personal Info: ${personalInfoFilter.charAt(0).toUpperCase() + personalInfoFilter.slice(1)}` : 'Personal Info'}
                          </span>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All</SelectItem>
                          <SelectItem value="yes">Yes</SelectItem>
                          <SelectItem value="no">No</SelectItem>
                        </SelectContent>
                      </Select>

                      {/* Add Button */}
                      <Button
                        variant="default"
                        onClick={() => setShowAdd(true)}
                        className="whitespace-nowrap"
                      >
                        + Add Data Record Type
                      </Button>
                    </div>
                  </div>

                  <DataRetentionTable records={filteredRecords} onDelete={onDeleteClick} onView={record => {
                    setDialogRecord(record);
                    setDialogLinkedAssets((record as any).assets ?? []);
                    setDialogOpen(true);
                  }} />
                </>
              )}
            </TabsContent>

            <TabsContent value="pools" className="mt-4">
              {poolsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <Layers className="h-12 w-12 mx-auto mb-4 opacity-50 animate-pulse" />
                    <p className="text-muted-foreground">Loading data pools...</p>
                  </div>
                </div>
              ) : (
              <>
              <div className="flex flex-col sm:flex-row gap-4 sm:gap-0 sm:justify-between items-stretch sm:items-center mb-4">
                <div className="w-full sm:w-auto">
                  <Input
                    placeholder="Search data pools..."
                    value={poolSearch}
                    onChange={(e) => setPoolSearch(e.target.value)}
                    className="max-w-sm"
                  />
                </div>
                <Button
                  variant="default"
                  onClick={() => {
                    setEditingPool(null);
                    setPoolForm({ name: '', reason: '', status: 'active', dataTypes: [], systems: [] });
                    setShowAddPool(true);
                  }}
                  className="w-full sm:w-auto"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Data Pool
                </Button>
              </div>

              {filteredPools.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Layers className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No data pools found</p>
                  <p className="text-sm mt-1">Create a data pool to group related data types</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredPools.map(pool => (
                    <Card key={pool.id} className="bg-card/50 border-border/50">
                      <CardHeader className="pb-2">
                        <div className="flex items-start justify-between">
                          <div>
                            <CardTitle className="text-lg">{pool.name}</CardTitle>
                            {pool.reason && (
                              <CardDescription className="mt-1">{pool.reason}</CardDescription>
                            )}
                          </div>
                          <Badge variant={pool.status === 'active' ? 'default' : 'secondary'}>
                            {pool.status || 'active'}
                          </Badge>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          <div className="flex items-center gap-4 text-sm">
                            <div className="flex items-center gap-1">
                              <Database className="h-3.5 w-3.5 text-muted-foreground" />
                              <span className="text-muted-foreground">{pool.dataTypes?.length || 0} data types</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Layers className="h-3.5 w-3.5 text-muted-foreground" />
                              <span className="text-muted-foreground">{pool.systems?.length || 0} systems</span>
                            </div>
                          </div>
                          {pool.dataTypes && pool.dataTypes.length > 0 && (
                            <div>
                              <span className="text-xs text-muted-foreground">Data Types:</span>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {getDataTypeDisplayNames(pool.dataTypes).slice(0, 3).map(name => (
                                  <Badge key={name} variant="outline" className="text-xs bg-primary/5">{name}</Badge>
                                ))}
                                {pool.dataTypes.length > 3 && (
                                  <Badge variant="outline" className="text-xs">+{pool.dataTypes.length - 3}</Badge>
                                )}
                              </div>
                            </div>
                          )}
                          {pool.systems && pool.systems.length > 0 && (
                            <div>
                              <span className="text-xs text-muted-foreground">Systems:</span>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {getSystemDisplayNames(pool.systems).slice(0, 3).map(name => (
                                  <Badge key={name} variant="secondary" className="text-xs">{name}</Badge>
                                ))}
                                {pool.systems.length > 3 && (
                                  <Badge variant="secondary" className="text-xs">+{pool.systems.length - 3}</Badge>
                                )}
                              </div>
                            </div>
                          )}
                          {pool.labels && pool.labels.length > 0 && (
                            <div>
                              <span className="text-xs text-muted-foreground">Labels:</span>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {pool.labels.slice(0, 3).map(label => (
                                  <Badge key={label} variant="outline" className="text-xs">{label}</Badge>
                                ))}
                                {pool.labels.length > 3 && (
                                  <Badge variant="outline" className="text-xs">+{pool.labels.length - 3}</Badge>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="flex gap-2 mt-4 pt-4 border-t border-border/50">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openEditPool(pool)}
                            className="flex-1"
                          >
                            <Edit2 className="h-3 w-3 mr-1" />
                            Edit
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeletePool(pool.id)}
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
              </>
              )}
            </TabsContent>
            </Tabs>
          </div>
          <DataRetentionCreateDialog
            open={showAdd}
            onClose={() => setShowAdd(false)}
            onCreate={async (newRecord, linkedSuppliers, linkedAssets) => {
              // Optimistic update: immediately add to local state
              setRecords(prev => [...prev, newRecord]);
              setShowAdd(false);
              
              // Sync with server in background
              try {
                await dataRetentionService.createDataType(newRecord.dataTypeID, newRecord);
                // Background refresh for consistency (non-blocking, no loading indicator)
                loadRecords(false);
              } catch (error) {
                console.error('Failed to create data type:', error);
                toast.error('Failed to save - please try again');
                // Revert optimistic update on error
                setRecords(prev => prev.filter(r => r.dataTypeID !== newRecord.dataTypeID));
              }
            }}
            assets={[]}
            suppliers={[]}
          />
          <DataRetentionDialog
            open={dialogOpen}
            record={dialogRecord}
            suppliers={dialogLinkedAssets}
            assets={dialogLinkedAssets}
            onClose={() => setDialogOpen(false)}
            onSave={async (updated, linkedSuppliers, linkedAssets) => {
              // Optimistic update: immediately update local state
              const previousRecords = records;
              setRecords(prev => prev.map(r => r.dataTypeID === updated.dataTypeID ? { ...updated } : r));
              setDialogOpen(false);
              
              // Sync with server in background
              try {
                await dataRetentionService.updateDataType(updated.dataTypeID, updated);
                // Background refresh for consistency (non-blocking, no loading indicator)
                loadRecords(false);
              } catch (error) {
                console.error('Failed to update data type:', error);
                toast.error('Failed to save changes - please try again');
                // Revert optimistic update on error
                setRecords(previousRecords);
              }
            }}
          />
        </section>
        <DeleteDataItemConfirmationDialog show={showDeleteDataItemDialog.show} callback={deleteDataItem} />
        
        {/* Data Pool Create/Edit Dialog */}
        <Dialog open={showAddPool} onOpenChange={setShowAddPool}>
          <DialogContent className="bg-card max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editingPool ? 'Edit Data Pool' : 'Create Data Pool'}</DialogTitle>
              <DialogDescription>
                {editingPool ? 'Update the data pool details below.' : 'Create a new data pool to group related data types.'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4 max-h-[60vh] overflow-y-auto pr-2">
              <div className="space-y-2">
                <Label htmlFor="pool-name">Name *</Label>
                <Input
                  id="pool-name"
                  placeholder="e.g., Customer Data Pool"
                  value={poolForm.name || ''}
                  onChange={(e) => setPoolForm(prev => ({ ...prev, name: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="pool-reason">Description</Label>
                <Input
                  id="pool-reason"
                  placeholder="Brief description of this data pool"
                  value={poolForm.reason || ''}
                  onChange={(e) => setPoolForm(prev => ({ ...prev, reason: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Data Types</Label>
                <DataTypeCombobox
                  selected={poolForm.dataTypes || []}
                  setSelected={(val) => {
                    const newVal = typeof val === 'function' ? val(poolForm.dataTypes || []) : val;
                    setPoolForm(prev => ({ ...prev, dataTypes: newVal }));
                  }}
                  placeholder="Select data types to include..."
                  fullWidth
                />
                <p className="text-xs text-muted-foreground">Select the data types that belong to this pool</p>
              </div>
              <div className="space-y-2">
                <Label>Systems / Assets</Label>
                <AssetsCombobox
                  selected={poolForm.systems || []}
                  setSelected={(val) => {
                    const newVal = typeof val === 'function' ? val(poolForm.systems || []) : val;
                    setPoolForm(prev => ({ ...prev, systems: newVal }));
                  }}
                  placeholder="Select systems/assets..."
                  fullWidth
                />
                <p className="text-xs text-muted-foreground">Select the systems or assets associated with this pool</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="pool-status">Status</Label>
                <Select
                  value={poolForm.status || 'active'}
                  onValueChange={(v) => setPoolForm(prev => ({ ...prev, status: v as 'active' | 'inactive' }))}
                >
                  <SelectTrigger>
                    <span>{poolForm.status === 'active' ? 'Active' : 'Inactive'}</span>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={() => {
                setShowAddPool(false);
                setEditingPool(null);
                setPoolForm({ name: '', reason: '', status: 'active', dataTypes: [], systems: [] });
              }}>
                Cancel
              </Button>
              <Button onClick={handleSavePool}>
                {editingPool ? 'Update' : 'Create'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AssetsProvider>
  </DataTypesProvider>
  );
};

export default DataRetentionSchedule;

type DeleteDataItemConfirmationDialogProps = {
  show: boolean;
  callback: (confirmed: boolean) => void;
};
const DeleteDataItemConfirmationDialog = ({ show, callback }: DeleteDataItemConfirmationDialogProps) => {
  return (
    <Dialog open={show}>
      <DialogContent className="bg-card">
        <DialogHeader>
          <DialogTitle>Delete Item</DialogTitle>
          <DialogDescription>
            Are you sure you want to delete this item?
          </DialogDescription>
        </DialogHeader>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={() => callback(false) }>Cancel</Button>
          <Button
            variant="destructive"
            onClick={() => callback(true)}
          >
            Delete
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
